import { EventEmitter, Injectable } from '@angular/core';
import { HttpService, Search } from '@docpath/linces-angular-components/common';
import { Observable } from 'rxjs';
import { Dge } from 'src/app/pages/configuration/engines/models/dge';

import { EndPoints } from '../../../constants/end-points';
import { Job, JobConfiguration } from '../models/job';
import { StatisticsCardResult, StatisticsCardSearch, StatisticsResult, StatisticsSearch } from '../models/statistics';
import { StatisticsService } from './statistics.service';

@Injectable({
    providedIn: 'root'
})
export class StatisticsHttpService {
    readonly relativeUrl = `${EndPoints.ENDPOINT_WEB_API}/${EndPoints.ENDPOINT_STATISTICS}`;

    showLoadingTableChanged: EventEmitter<boolean> = new EventEmitter();

    constructor(
        private readonly httpService: HttpService,
        private readonly statisticsService: StatisticsService
    ) { }

    getStatistics(search: StatisticsSearch): Observable<StatisticsResult> {

        return new Observable(observer => {
            const searchBase64 = btoa(JSON.stringify(search));

            this.httpService.get({
                relativeUrl: `${this.relativeUrl}`,
                queryParameters: { search: searchBase64 },
                suscribe: response => {
                    this.statisticsService.chartReadyToRefresh(search.key);
                    const result = response.result as unknown as StatisticsResult;
                    observer.next(result);
                    observer.complete();
                }, suscribeOnError: () => {
                    this.statisticsService.chartReadyToRefresh(search.key);
                    observer.complete();
                }
            });
        });

    }

    getAllDge(): Observable<Dge[]> {

        return new Observable(observer => {
            this.httpService.get({
                relativeUrl: `${EndPoints.ENDPOINT_WEB_API}/${EndPoints.ENDPOINT_CONFIGURATION}/${EndPoints.ENDPOINT_DGE}/${EndPoints.ENDPOINT_ALL}`,
                suscribe: response => {
                    const result = response.result as unknown as Dge[];

                    observer.next(result);
                    observer.complete();
                }
            });
        });

    }

    // eslint-disable-next-line max-params, sonarjs/sonar-max-params
    getJobs(loading: boolean, id: number, timeInterval: number, customNumberHoursFrom: string, customNumberHoursTo: string, lastJobId: number, search: Search, suscribe: (result: Job[], totalSize: number) => void): void {
        const searchBase64 = btoa(JSON.stringify(search));

        // Show spinner loading only first time
        if (loading) {
            this.showLoadingTableChanged.emit(true);
        }
        
        this.httpService.get({
            relativeUrl: `${this.relativeUrl}/${EndPoints.ENDPOINT_STATISTICS_JOB}`,
            queryParameters: { idDGE: id, time: timeInterval, customNumberHoursFrom, customNumberHoursTo, lastJobId, search: searchBase64 },
            suscribe: response => {
                const responseBody = response.result as unknown as Search,
                    result = responseBody.result as Job[],
                    totalSize = responseBody.paginator?.totalSize ?? 0;
                this.showLoadingTableChanged.emit(false);
                suscribe(result, totalSize);
            },
            suscribeOnError: () => {
                this.showLoadingTableChanged.emit(false);
            }
        });
    }

    hasRelaunchedJobs(id: number): Observable<boolean> {
        return new Observable(observer => {
            this.httpService.get({
                relativeUrl: `${this.relativeUrl}/${EndPoints.ENDPOINT_STATISTICS_HAS_RELAUNCHED_JOBS}`,
                queryParameters: { id },
                loading: true,
                suscribe: response => {
                    const result = response.result as unknown as boolean;
                    observer.next(result);
                    observer.complete();
                }
            });
        });

    }

    getRelaunchedJobs(id: number): Observable<Job[]> {
        return new Observable(observer => {
            this.httpService.get({
                relativeUrl: `${this.relativeUrl}/${EndPoints.ENDPOINT_STATISTICS_RELAUNCHED_JOBS}`,
                queryParameters: { id },
                loading: true,
                suscribe: response => {
                    const result = response.result as unknown as Job[];
                    observer.next(result);
                    observer.complete();
                }
            });
        });

    }

    getJobConfiguration(id: number): Observable<JobConfiguration> {
        return new Observable(observer => {
            this.httpService.get({
                relativeUrl: `${this.relativeUrl}/${EndPoints.ENDPOINT_STATISTICS_CONFIGURATION_JOB}`,
                queryParameters: { id },
                loading: true,
                suscribe: response => {
                    const jobConfiguration = response.result as unknown as JobConfiguration;
                    observer.next(jobConfiguration);
                    observer.complete();
                }
            });
        });
    }

    relaunchJob(jobConfiguration: JobConfiguration): Observable<void> {
        return new Observable(observer => {
            this.httpService.post(jobConfiguration, {
                relativeUrl: `${this.relativeUrl}/${EndPoints.ENDPOINT_STATISTICS_RELAUNCH_JOB}`,
                loading: true,
                suscribe: () => {
                    observer.next();
                    observer.complete();
                },
                toast: 'statistics.toast.relaunch-success'
            });
        });
    }

    getCardsInfo(search: StatisticsCardSearch): Observable<StatisticsCardResult> {
        return new Observable(observer => {
            const searchBase64 = btoa(JSON.stringify(search));

            this.httpService.get({
                relativeUrl: `${this.relativeUrl}/${EndPoints.ENDPOINT_STATISTICS_CARDS}`,
                queryParameters: { search: searchBase64 },
                suscribe: response => {
                    this.statisticsService.cardReadyToRefresh();
                    const result = response.result as unknown as StatisticsCardResult;
                    observer.next(result);
                    observer.complete();
                },
                suscribeOnError: () => {
                    this.statisticsService.cardReadyToRefresh();
                    observer.next(new StatisticsCardResult(0, 0, 0, 0, 0));
                    observer.complete();
                }
            });
        });
    }

    getLastJobId(id: number): Observable<number> {
        return new Observable(observer => {
            this.httpService.get({
                relativeUrl: `${this.relativeUrl}/${EndPoints.ENDPOINT_STATISTICS_LAST_JOB_ID}`,
                queryParameters: { dgeId: id },
                loading: true,
                suscribe: response => {
                    const lastJobId = response.result as unknown as number;
                    observer.next(lastJobId);
                    observer.complete();
                }
            });
        });
    }

}